import { useMemo, useState } from 'react'
import { OrganizationProfile, useAuth } from '@clerk/clerk-react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { Loader2, Pencil, Search } from 'lucide-react'

import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/card'
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Switch } from '@/components/ui/switch'
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '@/components/ui/tabs'
import { Textarea } from '@/components/ui/textarea'
import { useCanManageIntegrations, useHomeV2Preview } from '@/lib/auth'
import { formatRelative } from '@/lib/dates'
import {
  BUCKET_ORDER,
  DEFAULT_PERMISSIONS,
  type EricPermissions,
} from '@/lib/eric-permissions'
import {
  currentSubscriptionEndpoint,
  isIosSafariNotInstalled,
  isPushSupported,
  pushPermission,
  subscribeThisDevice,
  unsubscribeThisDevice,
  type PushSubscriptionInfo,
} from '@/lib/push'
import { PhoneLink } from '@/components/PhoneLink'

type Shop = {
  id: string
  name: string
  owner_name: string | null
  address: string | null
  city: string | null
  province: string | null
  postal_code: string | null
  country: string | null
  phone: string | null
  email: string | null
  website: string | null
  twilio_number: string | null
  labor_rate_cad: number | null
  tax_rate: number | null
  tax_label: string | null
  currency: string | null
  timezone: string | null
}

type Dealer = {
  id: string
  name: string
  brand: string | null
  city: string | null
  province: string | null
  parts_phone: string | null
  hours_parts: string | null
  refused_ai: boolean
  notes: string | null
  ivr_hint: string | null
  calls_to_date: number
  successful_calls: number
}

type LaborMatrixEntry = {
  id: string
  year: number | null
  make: string | null
  model: string | null
  engine: string | null
  job: string
  hours: number | null
  source: string
  confidence: string | null
  hits: number | null
  last_used_at: string | null
  updated_at: string
}

// Tenant-facing integrations tab now lists ONLY BYOK supplier
// integrations (today: WorldPac). Platform infrastructure (Clerk,
// Neon, Sentry, Anthropic, Vapi, Twilio, R2, Cloudflare Email) is
// platform-managed — it lives on /admin/integrations, gated by
// `useIsPlatformAdmin()` + Worker `requirePlatformAdmin` middleware.
// See worker/routes/integrations.ts + worker/routes/admin/integrations.ts.
type SupplierStatus = {
  supplier: string
  connected: boolean
}

type SupplierStatusResponse = {
  suppliers: SupplierStatus[]
}

export function Settings() {
  const { t } = useTranslation('settings')
  const canManageIntegrations = useCanManageIntegrations()

  // The Worker also gates PATCH /api/shop with requireRole(...OWNER_ROLES),
  // so a non-owner UI tweak can't bypass — but we still hide the
  // edit affordances for clarity.
  return (
    <div className="space-y-4">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <h1 className="text-[22px] font-bold tracking-[-0.015em] text-foreground [text-wrap:balance]">
            {t('header.title')}
          </h1>
          <p className="mt-0.5 text-[13px] text-muted-foreground">
            {t('header.subtitle')}
          </p>
        </div>
      </div>

      {!canManageIntegrations ? (
        <div className="rounded-[10px] border border-border bg-muted p-3 text-sm text-muted-foreground">
          {t('header.read_only_hint')}
        </div>
      ) : null}

      <Tabs defaultValue="general" className="w-full">
        <TabsList className="w-full sm:w-auto">
          <TabsTrigger value="general">{t('tabs.general')}</TabsTrigger>
          <TabsTrigger value="team">{t('tabs.team')}</TabsTrigger>
          <TabsTrigger value="integrations">
            {t('tabs.integrations')}
          </TabsTrigger>
          <TabsTrigger value="estimator">{t('tabs.estimator')}</TabsTrigger>
          <TabsTrigger value="eric_permissions">
            {t('tabs.eric_permissions')}
          </TabsTrigger>
          <TabsTrigger value="notifications">
            {t('tabs.notifications')}
          </TabsTrigger>
        </TabsList>

        <TabsContent value="general">
          <HomeInterfaceCard />
          <GeneralTab readOnly={!canManageIntegrations} />
        </TabsContent>

        <TabsContent value="team">
          <TeamTab />
        </TabsContent>

        <TabsContent value="integrations">
          <IntegrationsTab />
        </TabsContent>

        <TabsContent value="estimator">
          <EstimatorTab readOnly={!canManageIntegrations} />
        </TabsContent>

        <TabsContent value="eric_permissions">
          <EricPermissionsTab readOnly={!canManageIntegrations} />
        </TabsContent>

        <TabsContent value="notifications">
          <NotificationsTab />
        </TabsContent>
      </Tabs>
    </div>
  )
}

// ─────────────────────────────────────────────────────────────────────
// Home interface (per-user Home v2 preview toggle)

/**
 * Per-user toggle for the Home v2 preview (on by default). Writes the
 * signed-in user's Clerk `unsafeMetadata.home_v2` (see
 * `useHomeV2Preview`), so it is personal — flipping it changes only this
 * user's home, never the shop's. Lives in the General tab because
 * Settings is owner-gated (Emil reaches it as the EMSERVICE owner). The
 * Switch disables while the metadata write is in flight.
 */
function HomeInterfaceCard() {
  const { t } = useTranslation('settings')
  const { enabled, isLoaded, setEnabled } = useHomeV2Preview()
  const [saving, setSaving] = useState(false)
  return (
    <Card className="mb-4 flex flex-row items-start justify-between gap-4 rounded-[14px] ring-border p-4">
      <div className="min-w-0">
        <Label
          htmlFor="home-v2-toggle"
          className="text-sm font-semibold text-foreground"
        >
          {t('home_v2.title')}
        </Label>
        <p className="mt-1 text-sm text-muted-foreground">
          {t('home_v2.description')}
        </p>
      </div>
      <Switch
        id="home-v2-toggle"
        checked={enabled}
        disabled={!isLoaded || saving}
        onCheckedChange={(next) => {
          setSaving(true)
          void setEnabled(next).finally(() => setSaving(false))
        }}
        aria-label={t('home_v2.title')}
      />
    </Card>
  )
}

// ─────────────────────────────────────────────────────────────────────
// General

function GeneralTab({ readOnly }: { readOnly: boolean }) {
  const { t } = useTranslation('settings')
  const { t: tCommon } = useTranslation('common')
  const { getToken, isLoaded } = useAuth()
  const qc = useQueryClient()

  const shopQuery = useQuery({
    queryKey: ['settings', 'shop'],
    enabled: isLoaded,
    queryFn: async () => {
      const token = await getToken()
      if (!token) throw new Error(tCommon('error.missing_token'))
      const res = await fetch('/api/shop', {
        headers: { Authorization: `Bearer ${token}` },
      })
      if (!res.ok) {
        const text = await res.text().catch(() => '')
        throw new Error(
          text || tCommon('error.request_failed', { status: res.status }),
        )
      }
      return (await res.json()) as Shop
    },
  })

  const [form, setForm] = useState<Partial<Shop>>({})
  const [taxRatePct, setTaxRatePct] = useState<string>('')
  const [submitError, setSubmitError] = useState<string | null>(null)

  // Hydrate the local form once data is fetched. Tax rate is stored as
  // a 0..1 decimal in the DB; the form surface uses percent so the
  // mechanic can type "13" rather than "0.13". In-render setState —
  // React discards the about-to-paint and re-renders with form
  // populated; the empty-form guard turns this into a one-shot, same
  // semantics as the old effect.
  if (shopQuery.data && Object.keys(form).length === 0) {
    setForm(shopQuery.data)
    setTaxRatePct(
      shopQuery.data.tax_rate === null
        ? ''
        : (shopQuery.data.tax_rate * 100).toString(),
    )
  }

  const saveMutation = useMutation({
    mutationFn: async (payload: Record<string, unknown>) => {
      const token = await getToken()
      if (!token) throw new Error(tCommon('error.missing_token'))
      const res = await fetch('/api/shop', {
        method: 'PATCH',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      })
      if (!res.ok) {
        const text = await res.text().catch(() => '')
        throw new Error(
          text || tCommon('error.request_failed', { status: res.status }),
        )
      }
      return res.json()
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['settings', 'shop'] })
    },
  })

  const handleSave = (e: React.FormEvent) => {
    e.preventDefault()
    setSubmitError(null)

    const payload: Record<string, unknown> = {
      name: form.name,
      owner_name: form.owner_name ?? null,
      address: form.address ?? null,
      city: form.city ?? null,
      province: form.province ?? null,
      postal_code: form.postal_code ?? null,
      phone: form.phone ?? null,
      email: form.email ?? null,
    }
    if (form.labor_rate_cad !== null && form.labor_rate_cad !== undefined) {
      payload.labor_rate_cad = form.labor_rate_cad
    }
    const taxNum = Number(taxRatePct)
    if (taxRatePct !== '' && Number.isFinite(taxNum)) {
      payload.tax_rate = taxNum / 100
    }
    saveMutation.mutate(payload, {
      onError: (err) => {
        setSubmitError(err instanceof Error ? err.message : String(err))
      },
    })
  }

  if (shopQuery.isLoading) {
    return <Card className="rounded-[14px] ring-border p-5 text-sm text-muted-foreground">{tCommon('loading')}</Card>
  }
  if (shopQuery.isError || !shopQuery.data) {
    return (
      <Card className="rounded-[14px] ring-border p-5 text-sm text-destructive">
        {shopQuery.error instanceof Error
          ? shopQuery.error.message
          : t('general.error')}
      </Card>
    )
  }

  const shop = shopQuery.data

  return (
    <form onSubmit={handleSave} className="space-y-4">
      <Card className="rounded-[14px] ring-border p-5">
        <div className="text-[13px] font-semibold text-muted-foreground">
          {t('general.identity_header')}
        </div>
        <div className="mt-3 grid gap-3 sm:grid-cols-2">
          <Field label={t('general.fields.name')} htmlFor="shop-name">
            <Input
              id="shop-name"
              value={form.name ?? ''}
              onChange={(e) =>
                setForm((f) => ({ ...f, name: e.target.value }))
              }
              disabled={readOnly}
              required
            />
          </Field>
          <Field
            label={t('general.fields.owner_name')}
            htmlFor="shop-owner"
          >
            <Input
              id="shop-owner"
              value={form.owner_name ?? ''}
              onChange={(e) =>
                setForm((f) => ({ ...f, owner_name: e.target.value }))
              }
              disabled={readOnly}
            />
          </Field>
          <Field label={t('general.fields.email')} htmlFor="shop-email">
            <Input
              id="shop-email"
              type="email"
              value={form.email ?? ''}
              onChange={(e) =>
                setForm((f) => ({ ...f, email: e.target.value }))
              }
              disabled={readOnly}
            />
          </Field>
          <Field label={t('general.fields.phone')} htmlFor="shop-phone">
            <Input
              id="shop-phone"
              value={form.phone ?? ''}
              onChange={(e) =>
                setForm((f) => ({ ...f, phone: e.target.value }))
              }
              disabled={readOnly}
            />
            <div className="mt-1 text-xs text-muted-foreground">
              <PhoneLink phone={shop.phone} />
            </div>
          </Field>
        </div>
      </Card>

      <Card className="rounded-[14px] ring-border p-5">
        <div className="text-[13px] font-semibold text-muted-foreground">
          {t('general.address_header')}
        </div>
        <div className="mt-3 grid gap-3 sm:grid-cols-2">
          <Field label={t('general.fields.address')} htmlFor="shop-address">
            <Input
              id="shop-address"
              value={form.address ?? ''}
              onChange={(e) =>
                setForm((f) => ({ ...f, address: e.target.value }))
              }
              disabled={readOnly}
            />
          </Field>
          <Field label={t('general.fields.city')} htmlFor="shop-city">
            <Input
              id="shop-city"
              value={form.city ?? ''}
              onChange={(e) =>
                setForm((f) => ({ ...f, city: e.target.value }))
              }
              disabled={readOnly}
            />
          </Field>
          <Field
            label={t('general.fields.province')}
            htmlFor="shop-province"
          >
            <Input
              id="shop-province"
              value={form.province ?? ''}
              onChange={(e) =>
                setForm((f) => ({ ...f, province: e.target.value }))
              }
              disabled={readOnly}
            />
          </Field>
          <Field
            label={t('general.fields.postal_code')}
            htmlFor="shop-postal"
          >
            <Input
              id="shop-postal"
              value={form.postal_code ?? ''}
              onChange={(e) =>
                setForm((f) => ({ ...f, postal_code: e.target.value }))
              }
              disabled={readOnly}
            />
          </Field>
        </div>
      </Card>

      <Card className="rounded-[14px] ring-border p-5">
        <div className="text-[13px] font-semibold text-muted-foreground">
          {t('general.estimator_header')}
        </div>
        <p className="mt-1 text-xs text-muted-foreground">
          {t('general.estimator_caption')}
        </p>
        <div className="mt-3 grid gap-3 sm:grid-cols-3">
          <Field
            label={t('general.fields.labor_rate')}
            htmlFor="shop-labor-rate"
          >
            <Input
              id="shop-labor-rate"
              type="number"
              step="0.01"
              min="0"
              value={form.labor_rate_cad ?? ''}
              onChange={(e) =>
                setForm((f) => ({
                  ...f,
                  labor_rate_cad:
                    e.target.value === '' ? null : Number(e.target.value),
                }))
              }
              disabled={readOnly}
            />
            <div className="mt-1 text-xs text-muted-foreground">
              {t('general.fields.labor_rate_caption')}
            </div>
          </Field>
          <Field
            label={t('general.fields.tax_rate')}
            htmlFor="shop-tax-rate"
          >
            <Input
              id="shop-tax-rate"
              type="number"
              step="0.01"
              min="0"
              max="100"
              value={taxRatePct}
              onChange={(e) => setTaxRatePct(e.target.value)}
              disabled={readOnly}
            />
            <div className="mt-1 text-xs text-muted-foreground">
              {t('general.fields.tax_rate_caption', {
                label: shop.tax_label ?? 'HST',
              })}
            </div>
          </Field>
          <Field
            label={t('general.fields.currency')}
            htmlFor="shop-currency"
          >
            <Input
              id="shop-currency"
              value={shop.currency ?? 'CAD'}
              disabled
            />
            <div className="mt-1 text-xs text-muted-foreground">
              {shop.timezone ?? '—'}
            </div>
          </Field>
        </div>
      </Card>

      {submitError ? (
        <div className="rounded-[10px] border border-destructive/30 bg-destructive/10 p-3 text-sm text-destructive">
          {submitError}
        </div>
      ) : null}

      {saveMutation.isSuccess ? (
        <div className="rounded-[10px] border border-border bg-muted p-3 text-sm text-foreground">
          {t('general.saved')}
        </div>
      ) : null}

      {!readOnly ? (
        <div className="flex items-center gap-3">
          <Button type="submit" disabled={saveMutation.isPending}>
            {saveMutation.isPending ? (
              <>
                <Loader2 className="size-4 animate-spin" />
                {t('general.saving')}
              </>
            ) : (
              t('general.save')
            )}
          </Button>
        </div>
      ) : null}
    </form>
  )
}

function Field({
  label,
  htmlFor,
  children,
}: {
  label: string
  htmlFor: string
  children: React.ReactNode
}) {
  return (
    <div className="space-y-1.5">
      <Label htmlFor={htmlFor}>{label}</Label>
      {children}
    </div>
  )
}

// ─────────────────────────────────────────────────────────────────────
// Team — Clerk's prebuilt OrganizationProfile drops in members,
// invites, and role assignment for free. Don't reinvent.

function TeamTab() {
  const { t } = useTranslation('settings')
  return (
    <Card className="rounded-[14px] ring-border p-5">
      <div className="text-[13px] font-semibold text-muted-foreground">
        {t('team.header')}
      </div>
      <p className="mt-1 text-xs text-muted-foreground">
        {t('team.subtitle')}
      </p>
      <div className="mt-4">
        <OrganizationProfile />
      </div>
    </Card>
  )
}

// ─────────────────────────────────────────────────────────────────────
// Integrations — tenant BYOK supplier credentials.
//
// Each row is a supplier the tenant can connect with their own API key
// (their own dealer relationship). Storage is encrypted at rest per
// shop — see worker/lib/credentials.ts + worker/lib/crypto.ts.

function IntegrationsTab() {
  const { t } = useTranslation('settings')
  const { t: tCommon } = useTranslation('common')
  const canManage = useCanManageIntegrations()
  const { getToken, isLoaded } = useAuth()
  const qc = useQueryClient()
  const [editing, setEditing] = useState<string | null>(null)
  const [disconnecting, setDisconnecting] = useState<string | null>(null)

  const statusQuery = useQuery({
    queryKey: ['settings', 'integrations'],
    enabled: isLoaded,
    queryFn: async () => {
      const token = await getToken()
      if (!token) throw new Error(tCommon('error.missing_token'))
      const res = await fetch('/api/integrations', {
        headers: { Authorization: `Bearer ${token}` },
      })
      if (!res.ok) {
        const text = await res.text().catch(() => '')
        throw new Error(
          text || tCommon('error.request_failed', { status: res.status }),
        )
      }
      return (await res.json()) as SupplierStatusResponse
    },
  })

  const connectMutation = useMutation({
    mutationFn: async ({
      supplier,
      apiKey,
    }: {
      supplier: string
      apiKey: string
    }) => {
      const token = await getToken()
      if (!token) throw new Error(tCommon('error.missing_token'))
      const res = await fetch(`/api/integrations/${supplier}`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ api_key: apiKey }),
      })
      if (!res.ok) {
        const text = await res.text().catch(() => '')
        throw new Error(
          text || tCommon('error.request_failed', { status: res.status }),
        )
      }
      return res.json()
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['settings', 'integrations'] })
      setEditing(null)
    },
  })

  const disconnectMutation = useMutation({
    mutationFn: async (supplier: string) => {
      const token = await getToken()
      if (!token) throw new Error(tCommon('error.missing_token'))
      const res = await fetch(`/api/integrations/${supplier}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      })
      if (!res.ok) {
        const text = await res.text().catch(() => '')
        throw new Error(
          text || tCommon('error.request_failed', { status: res.status }),
        )
      }
      return res.json()
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['settings', 'integrations'] })
      setDisconnecting(null)
    },
  })

  const editingConnected =
    statusQuery.data?.suppliers.find((s) => s.supplier === editing)
      ?.connected ?? false

  return (
    <div className="space-y-4">
      <div>
        <h2 className="text-base font-semibold text-foreground">
          {t('integrations.header')}
        </h2>
        <p className="mt-1 text-xs text-muted-foreground">
          {t('integrations.subtitle')}
        </p>
      </div>

      {statusQuery.isError ? (
        <div className="rounded-[10px] border border-destructive/30 bg-destructive/10 p-3 text-sm text-destructive">
          {statusQuery.error instanceof Error
            ? statusQuery.error.message
            : t('integrations.error')}
        </div>
      ) : null}

      {statusQuery.isLoading ? (
        <Card className="rounded-[14px] ring-border p-5 text-sm text-muted-foreground">
          {tCommon('loading')}
        </Card>
      ) : null}

      {statusQuery.data ? (
        <div className="grid gap-3 sm:grid-cols-2">
          {statusQuery.data.suppliers.map((s) => (
            <Card
              key={s.supplier}
              className="flex flex-col gap-3 p-4 sm:flex-row sm:items-start sm:justify-between"
            >
              <div className="min-w-0">
                <div className="font-medium text-foreground">
                  {t(`integrations.list.${s.supplier}.name`)}
                </div>
                <div className="mt-0.5 text-xs text-muted-foreground">
                  {t(`integrations.list.${s.supplier}.description`)}
                </div>
                <div className="mt-2">
                  <span className="inline-block rounded-full border border-border bg-muted px-[9px] py-[3px] text-[12px] font-[650] text-muted-foreground">
                    {s.connected
                      ? t('integrations.connected')
                      : t('integrations.not_connected')}
                  </span>
                </div>
              </div>
              {canManage ? (
                <div className="flex shrink-0 flex-row gap-2 sm:flex-col">
                  <Button
                    type="button"
                    variant={s.connected ? 'outline' : 'default'}
                    size="sm"
                    onClick={() => setEditing(s.supplier)}
                  >
                    {s.connected
                      ? t('integrations.edit')
                      : t('integrations.connect')}
                  </Button>
                  {s.connected ? (
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => setDisconnecting(s.supplier)}
                    >
                      {t('integrations.disconnect')}
                    </Button>
                  ) : null}
                </div>
              ) : null}
            </Card>
          ))}
        </div>
      ) : null}

      <SupplierConnectDialog
        supplier={editing}
        connectedAlready={editingConnected}
        onClose={() => setEditing(null)}
        onSave={(apiKey) => {
          if (editing) connectMutation.mutate({ supplier: editing, apiKey })
        }}
        saving={connectMutation.isPending}
        error={
          connectMutation.error instanceof Error
            ? connectMutation.error.message
            : null
        }
      />

      <SupplierDisconnectDialog
        supplier={disconnecting}
        onClose={() => setDisconnecting(null)}
        onConfirm={() => {
          if (disconnecting) disconnectMutation.mutate(disconnecting)
        }}
        deleting={disconnectMutation.isPending}
      />
    </div>
  )
}

function SupplierConnectDialog({
  supplier,
  connectedAlready,
  onClose,
  onSave,
  saving,
  error,
}: {
  supplier: string | null
  connectedAlready: boolean
  onClose: () => void
  onSave: (apiKey: string) => void
  saving: boolean
  error: string | null
}) {
  const { t } = useTranslation('settings')
  const [apiKey, setApiKey] = useState('')
  // Clear the field whenever the dialog opens for a different
  // supplier (or re-opens for the same one). Keeps stale key
  // material out of the input across edits. Reset-on-prop-change is
  // expressed as in-render setState against a tracking snapshot —
  // React discards the about-to-paint and re-renders cleanly.
  const [prevSupplier, setPrevSupplier] = useState(supplier)
  if (supplier !== prevSupplier) {
    setPrevSupplier(supplier)
    setApiKey('')
  }

  const supplierName = supplier
    ? t(`integrations.list.${supplier}.name`)
    : ''
  const title = connectedAlready
    ? t('integrations.dialog.title_update', { supplier: supplierName })
    : t('integrations.dialog.title_connect', { supplier: supplierName })

  return (
    <Dialog
      open={supplier !== null}
      onOpenChange={(next) => {
        if (!next) onClose()
      }}
    >
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <Field
            label={t('integrations.dialog.api_key_label')}
            htmlFor="supplier-api-key"
          >
            <Input
              id="supplier-api-key"
              type="password"
              value={apiKey}
              onChange={(e) => setApiKey(e.target.value)}
              placeholder={t('integrations.dialog.api_key_placeholder')}
              autoComplete="off"
              spellCheck={false}
            />
            <div className="mt-1 text-xs text-muted-foreground">
              {t('integrations.dialog.api_key_help')}
            </div>
          </Field>
          {error ? (
            <div className="text-xs text-destructive">{error}</div>
          ) : null}
        </div>
        <DialogFooter>
          <DialogClose asChild>
            <Button type="button" variant="outline">
              {t('integrations.dialog.cancel')}
            </Button>
          </DialogClose>
          <Button
            type="button"
            disabled={saving || apiKey.trim() === ''}
            onClick={() => onSave(apiKey.trim())}
          >
            {saving ? (
              <>
                <Loader2 className="size-4 animate-spin" />
                {t('integrations.dialog.saving')}
              </>
            ) : (
              t('integrations.dialog.save')
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

function SupplierDisconnectDialog({
  supplier,
  onClose,
  onConfirm,
  deleting,
}: {
  supplier: string | null
  onClose: () => void
  onConfirm: () => void
  deleting: boolean
}) {
  const { t } = useTranslation('settings')
  const supplierName = supplier
    ? t(`integrations.list.${supplier}.name`)
    : ''
  return (
    <Dialog
      open={supplier !== null}
      onOpenChange={(next) => {
        if (!next) onClose()
      }}
    >
      <DialogContent>
        <DialogHeader>
          <DialogTitle>
            {t('integrations.disconnect_dialog.title', {
              supplier: supplierName,
            })}
          </DialogTitle>
          <DialogDescription>
            {t('integrations.disconnect_dialog.body', {
              supplier: supplierName,
            })}
          </DialogDescription>
        </DialogHeader>
        <DialogFooter>
          <DialogClose asChild>
            <Button type="button" variant="outline">
              {t('integrations.disconnect_dialog.cancel')}
            </Button>
          </DialogClose>
          <Button
            type="button"
            variant="destructive"
            disabled={deleting}
            onClick={onConfirm}
          >
            {deleting ? <Loader2 className="size-4 animate-spin" /> : null}
            {t('integrations.disconnect_dialog.confirm')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

// ─────────────────────────────────────────────────────────────────────
// Eric Estimator — labor matrix + dealer call-script tuning

function EstimatorTab({ readOnly }: { readOnly: boolean }) {
  const { t } = useTranslation('settings')
  return (
    <div className="space-y-6">
      <LaborMatrixSection readOnly={readOnly} />
      <DealersSection readOnly={readOnly} />
      <p className="text-xs text-muted-foreground">{t('estimator.footnote')}</p>
    </div>
  )
}

function LaborMatrixSection({ readOnly }: { readOnly: boolean }) {
  const { t } = useTranslation('settings')
  const { t: tCommon } = useTranslation('common')
  const { getToken, isLoaded } = useAuth()
  const qc = useQueryClient()
  const [editing, setEditing] = useState<LaborMatrixEntry | null>(null)
  const [search, setSearch] = useState('')

  const matrixQuery = useQuery({
    queryKey: ['settings', 'labor-matrix'],
    enabled: isLoaded,
    queryFn: async () => {
      const token = await getToken()
      if (!token) throw new Error(tCommon('error.missing_token'))
      const res = await fetch('/api/labor-matrix', {
        headers: { Authorization: `Bearer ${token}` },
      })
      if (!res.ok) {
        const text = await res.text().catch(() => '')
        throw new Error(
          text || tCommon('error.request_failed', { status: res.status }),
        )
      }
      return (await res.json()) as LaborMatrixEntry[]
    },
  })

  const filtered = useMemo(() => {
    const data = matrixQuery.data ?? []
    const q = search.trim().toLowerCase()
    if (!q) return data
    return data.filter((row) => {
      const haystack = [
        row.year?.toString() ?? '',
        row.make ?? '',
        row.model ?? '',
        row.engine ?? '',
        row.job,
      ]
        .join(' ')
        .toLowerCase()
      return haystack.includes(q)
    })
  }, [matrixQuery.data, search])

  const updateMutation = useMutation({
    mutationFn: async (payload: { id: string; hours: number }) => {
      const token = await getToken()
      if (!token) throw new Error(tCommon('error.missing_token'))
      const res = await fetch(`/api/labor-matrix/${payload.id}`, {
        method: 'PATCH',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        // Manual override promotes the row above WorldPac in the
        // Estimator's preference order — track that intent in `source`.
        body: JSON.stringify({
          hours: payload.hours,
          source: 'mechanic',
          confidence: 'high',
        }),
      })
      if (!res.ok) {
        const text = await res.text().catch(() => '')
        throw new Error(
          text || tCommon('error.request_failed', { status: res.status }),
        )
      }
      return res.json()
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['settings', 'labor-matrix'] })
      setEditing(null)
    },
  })

  return (
    <Card className="rounded-[14px] ring-border p-5">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <div className="text-[13px] font-semibold text-muted-foreground">
            {t('estimator.labor_matrix.header')}
          </div>
          <p className="mt-0.5 text-xs text-muted-foreground">
            {t('estimator.labor_matrix.subtitle')}
          </p>
        </div>
        {/* §4.10 search input: 48px, 14px radius, leading ink-3 icon */}
        <div className="relative w-full sm:w-72">
          <Search
            aria-hidden="true"
            className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-ink-3"
          />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            type="search"
            placeholder={t('estimator.labor_matrix.search')}
            className="h-12 rounded-[14px] pl-9"
          />
        </div>
      </div>

      {matrixQuery.isError ? (
        <div className="mt-3 rounded-[10px] border border-destructive/30 bg-destructive/10 p-3 text-sm text-destructive">
          {matrixQuery.error instanceof Error
            ? matrixQuery.error.message
            : t('estimator.labor_matrix.error')}
        </div>
      ) : null}

      <div className="mt-4 overflow-x-auto">
        {filtered.length === 0 && !matrixQuery.isLoading ? (
          <div className="rounded-[14px] border border-dashed border-border bg-muted/30 p-8 text-center text-sm text-muted-foreground">
            {t('estimator.labor_matrix.empty')}
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-[12px] text-muted-foreground">
                <th className="px-2 py-2 font-semibold">
                  {t('estimator.labor_matrix.cols.vehicle')}
                </th>
                <th className="px-2 py-2 font-semibold">
                  {t('estimator.labor_matrix.cols.job')}
                </th>
                <th className="px-2 py-2 font-semibold">
                  {t('estimator.labor_matrix.cols.hours')}
                </th>
                <th className="px-2 py-2 font-semibold">
                  {t('estimator.labor_matrix.cols.source')}
                </th>
                <th className="px-2 py-2 font-semibold">
                  {t('estimator.labor_matrix.cols.hits')}
                </th>
                <th className="px-2 py-2" />
              </tr>
            </thead>
            <tbody>
              {filtered.map((row) => (
                <tr
                  key={row.id}
                  className="border-t border-border align-top"
                >
                  <td className="px-2 py-2 text-foreground">
                    {[row.year, row.make, row.model]
                      .filter(Boolean)
                      .join(' ') || '—'}
                    {row.engine ? (
                      <div className="text-xs text-muted-foreground">
                        {row.engine}
                      </div>
                    ) : null}
                  </td>
                  <td className="px-2 py-2 text-foreground">{row.job}</td>
                  <td className="px-2 py-2 font-mono text-foreground">
                    {row.hours === null ? '—' : row.hours.toFixed(2)}
                  </td>
                  <td className="px-2 py-2 text-muted-foreground">
                    {row.source}
                  </td>
                  <td className="px-2 py-2 text-muted-foreground">
                    {row.hits ?? 0}
                  </td>
                  <td className="px-2 py-2 text-right">
                    {!readOnly ? (
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon-sm"
                        onClick={() => setEditing(row)}
                        aria-label={t('estimator.labor_matrix.edit')}
                      >
                        <Pencil className="size-3.5" />
                      </Button>
                    ) : null}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <LaborMatrixEditDialog
        entry={editing}
        onClose={() => setEditing(null)}
        onSave={(hours) => {
          if (editing) updateMutation.mutate({ id: editing.id, hours })
        }}
        saving={updateMutation.isPending}
        error={
          updateMutation.error instanceof Error
            ? updateMutation.error.message
            : null
        }
      />
    </Card>
  )
}

function LaborMatrixEditDialog({
  entry,
  onClose,
  onSave,
  saving,
  error,
}: {
  entry: LaborMatrixEntry | null
  onClose: () => void
  onSave: (hours: number) => void
  saving: boolean
  error: string | null
}) {
  const { t } = useTranslation('settings')
  const [hours, setHours] = useState<string>('')
  // Reset the hours input whenever the dialog opens against a new
  // labor-matrix row. In-render setState against a tracking snapshot —
  // React discards the current paint and re-renders with the seeded
  // value.
  const [prevEntry, setPrevEntry] = useState(entry)
  if (entry !== prevEntry) {
    setPrevEntry(entry)
    setHours(entry?.hours?.toString() ?? '')
  }

  const open = entry !== null

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next) onClose()
      }}
    >
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t('estimator.labor_matrix.dialog.title')}</DialogTitle>
          <DialogDescription>
            {entry
              ? `${[entry.year, entry.make, entry.model]
                  .filter(Boolean)
                  .join(' ')} · ${entry.job}`
              : ''}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <Field
            label={t('estimator.labor_matrix.dialog.hours_label')}
            htmlFor="lm-hours"
          >
            <Input
              id="lm-hours"
              type="number"
              step="0.01"
              min="0"
              value={hours}
              onChange={(e) => setHours(e.target.value)}
            />
          </Field>
          {error ? (
            <div className="text-xs text-destructive">{error}</div>
          ) : null}
        </div>
        <DialogFooter>
          <DialogClose asChild>
            <Button type="button" variant="outline">
              {t('estimator.labor_matrix.dialog.cancel')}
            </Button>
          </DialogClose>
          <Button
            type="button"
            disabled={saving || hours === ''}
            onClick={() => {
              const n = Number(hours)
              if (Number.isFinite(n) && n >= 0) onSave(n)
            }}
          >
            {saving ? (
              <>
                <Loader2 className="size-4 animate-spin" />
                {t('estimator.labor_matrix.dialog.saving')}
              </>
            ) : (
              t('estimator.labor_matrix.dialog.save')
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

function DealersSection({ readOnly }: { readOnly: boolean }) {
  const { t } = useTranslation('settings')
  const { t: tCommon } = useTranslation('common')
  const { getToken, isLoaded } = useAuth()
  const qc = useQueryClient()
  const [editing, setEditing] = useState<Dealer | null>(null)
  const [search, setSearch] = useState('')

  const dealersQuery = useQuery({
    queryKey: ['settings', 'dealers'],
    enabled: isLoaded,
    queryFn: async () => {
      const token = await getToken()
      if (!token) throw new Error(tCommon('error.missing_token'))
      const res = await fetch('/api/dealers', {
        headers: { Authorization: `Bearer ${token}` },
      })
      if (!res.ok) {
        const text = await res.text().catch(() => '')
        throw new Error(
          text || tCommon('error.request_failed', { status: res.status }),
        )
      }
      return (await res.json()) as Dealer[]
    },
  })

  const filtered = useMemo(() => {
    const data = dealersQuery.data ?? []
    const q = search.trim().toLowerCase()
    if (!q) return data
    return data.filter((d) => {
      return (
        d.name.toLowerCase().includes(q) ||
        (d.brand ?? '').toLowerCase().includes(q) ||
        (d.city ?? '').toLowerCase().includes(q)
      )
    })
  }, [dealersQuery.data, search])

  const updateMutation = useMutation({
    mutationFn: async (payload: {
      id: string
      patch: Partial<Pick<Dealer, 'parts_phone' | 'hours_parts' | 'ivr_hint' | 'notes' | 'refused_ai'>>
    }) => {
      const token = await getToken()
      if (!token) throw new Error(tCommon('error.missing_token'))
      const res = await fetch(`/api/dealers/${payload.id}`, {
        method: 'PATCH',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload.patch),
      })
      if (!res.ok) {
        const text = await res.text().catch(() => '')
        throw new Error(
          text || tCommon('error.request_failed', { status: res.status }),
        )
      }
      return res.json()
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['settings', 'dealers'] })
      qc.invalidateQueries({ queryKey: ['dealers'] })
      setEditing(null)
    },
  })

  return (
    <Card className="rounded-[14px] ring-border p-5">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <div className="text-[13px] font-semibold text-muted-foreground">
            {t('estimator.dealers.header')}
          </div>
          <p className="mt-0.5 text-xs text-muted-foreground">
            {t('estimator.dealers.subtitle')}
          </p>
        </div>
        {/* §4.10 search input: 48px, 14px radius, leading ink-3 icon */}
        <div className="relative w-full sm:w-72">
          <Search
            aria-hidden="true"
            className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-ink-3"
          />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            type="search"
            placeholder={t('estimator.dealers.search')}
            className="h-12 rounded-[14px] pl-9"
          />
        </div>
      </div>

      {dealersQuery.isError ? (
        <div className="mt-3 rounded-[10px] border border-destructive/30 bg-destructive/10 p-3 text-sm text-destructive">
          {dealersQuery.error instanceof Error
            ? dealersQuery.error.message
            : t('estimator.dealers.error')}
        </div>
      ) : null}

      <div className="mt-4 grid gap-3 grid-cols-1 md:grid-cols-2">
        {filtered.map((d) => (
          <div
            key={d.id}
            className="rounded-[10px] border border-border bg-background p-3"
          >
            <div className="flex flex-wrap items-start justify-between gap-2">
              <div className="min-w-0">
                <div className="font-medium text-foreground">{d.name}</div>
                <div className="text-xs text-muted-foreground">
                  {[d.brand, d.city].filter(Boolean).join(' · ')}
                </div>
              </div>
              {d.refused_ai ? (
                <span className="shrink-0 rounded-full border border-border bg-muted px-[9px] py-[3px] text-[12px] font-[650] text-muted-foreground">
                  {t('estimator.dealers.refused_ai')}
                </span>
              ) : null}
            </div>
            <div className="mt-2 grid grid-cols-2 gap-2 text-xs">
              <div>
                <div className="text-[12px] font-medium text-ink-3">
                  {t('estimator.dealers.parts_phone')}
                </div>
                <div className="font-mono">
                  <PhoneLink phone={d.parts_phone} />
                </div>
              </div>
              <div>
                <div className="text-[12px] font-medium text-ink-3">
                  {t('estimator.dealers.hours_parts')}
                </div>
                <div>{d.hours_parts || '—'}</div>
              </div>
            </div>
            {d.notes ? (
              <div className="mt-2 rounded-[10px] border border-border bg-muted/40 p-2 text-xs italic text-muted-foreground">
                {d.notes}
              </div>
            ) : null}
            {!readOnly ? (
              <div className="mt-3 flex justify-end">
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => setEditing(d)}
                >
                  <Pencil className="size-3.5" />
                  {t('estimator.dealers.edit')}
                </Button>
              </div>
            ) : null}
          </div>
        ))}

        {filtered.length === 0 && !dealersQuery.isLoading ? (
          <div className="col-span-full rounded-[14px] border border-dashed border-border bg-muted/30 p-8 text-center text-sm text-muted-foreground">
            {t('estimator.dealers.empty')}
          </div>
        ) : null}
      </div>

      <DealerEditDialog
        dealer={editing}
        onClose={() => setEditing(null)}
        onSave={(patch) => {
          if (editing) updateMutation.mutate({ id: editing.id, patch })
        }}
        saving={updateMutation.isPending}
        error={
          updateMutation.error instanceof Error
            ? updateMutation.error.message
            : null
        }
      />
    </Card>
  )
}

function DealerEditDialog({
  dealer,
  onClose,
  onSave,
  saving,
  error,
}: {
  dealer: Dealer | null
  onClose: () => void
  onSave: (
    patch: Partial<
      Pick<Dealer, 'parts_phone' | 'hours_parts' | 'ivr_hint' | 'notes' | 'refused_ai'>
    >,
  ) => void
  saving: boolean
  error: string | null
}) {
  const { t } = useTranslation('settings')
  const [partsPhone, setPartsPhone] = useState('')
  const [hoursParts, setHoursParts] = useState('')
  const [ivrHint, setIvrHint] = useState('')
  const [notes, setNotes] = useState('')
  const [refusedAi, setRefusedAi] = useState(false)
  // Seed all five inputs whenever the dialog opens against a new
  // dealer row. In-render setState against a tracking snapshot —
  // React discards the about-to-paint and re-renders with the seeded
  // values. Matches the previous effect's semantics: when `dealer`
  // goes from a row → null (close), inputs keep their last values
  // (the dialog is hidden anyway).
  const [prevDealer, setPrevDealer] = useState(dealer)
  if (dealer !== prevDealer) {
    setPrevDealer(dealer)
    if (dealer) {
      setPartsPhone(dealer.parts_phone ?? '')
      setHoursParts(dealer.hours_parts ?? '')
      setIvrHint(dealer.ivr_hint ?? '')
      setNotes(dealer.notes ?? '')
      setRefusedAi(dealer.refused_ai)
    }
  }

  return (
    <Dialog
      open={dealer !== null}
      onOpenChange={(next) => {
        if (!next) onClose()
      }}
    >
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t('estimator.dealers.dialog.title')}</DialogTitle>
          <DialogDescription>{dealer?.name ?? ''}</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <Field
            label={t('estimator.dealers.dialog.parts_phone')}
            htmlFor="dealer-parts-phone"
          >
            <Input
              id="dealer-parts-phone"
              value={partsPhone}
              onChange={(e) => setPartsPhone(e.target.value)}
            />
          </Field>
          <Field
            label={t('estimator.dealers.dialog.hours_parts')}
            htmlFor="dealer-hours"
          >
            <Input
              id="dealer-hours"
              value={hoursParts}
              onChange={(e) => setHoursParts(e.target.value)}
            />
          </Field>
          <Field
            label={t('estimator.dealers.dialog.ivr_hint')}
            htmlFor="dealer-ivr"
          >
            <Input
              id="dealer-ivr"
              value={ivrHint}
              onChange={(e) => setIvrHint(e.target.value)}
              placeholder={t('estimator.dealers.dialog.ivr_placeholder')}
            />
          </Field>
          <Field
            label={t('estimator.dealers.dialog.notes')}
            htmlFor="dealer-notes"
          >
            <Textarea
              id="dealer-notes"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={3}
            />
          </Field>
          <div className="flex items-center justify-between rounded-[10px] border border-border p-3">
            <div className="min-w-0">
              <Label htmlFor="dealer-refused" className="cursor-pointer">
                {t('estimator.dealers.dialog.refused_ai')}
              </Label>
              <div className="mt-0.5 text-xs text-muted-foreground">
                {t('estimator.dealers.dialog.refused_ai_caption')}
              </div>
            </div>
            <Switch
              id="dealer-refused"
              checked={refusedAi}
              onCheckedChange={setRefusedAi}
            />
          </div>
          {error ? (
            <div className="text-xs text-destructive">{error}</div>
          ) : null}
        </div>
        <DialogFooter>
          <DialogClose asChild>
            <Button type="button" variant="outline">
              {t('estimator.dealers.dialog.cancel')}
            </Button>
          </DialogClose>
          <Button
            type="button"
            disabled={saving}
            onClick={() =>
              onSave({
                parts_phone: partsPhone.trim() === '' ? null : partsPhone,
                hours_parts: hoursParts.trim() === '' ? null : hoursParts,
                ivr_hint: ivrHint.trim() === '' ? null : ivrHint,
                notes: notes.trim() === '' ? null : notes,
                refused_ai: refusedAi,
              })
            }
          >
            {saving ? (
              <>
                <Loader2 className="size-4 animate-spin" />
                {t('estimator.dealers.dialog.saving')}
              </>
            ) : (
              t('estimator.dealers.dialog.save')
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

// ─────────────────────────────────────────────────────────────────────
// Eric's Permissions — per-tenant tool-bucket gates.
//
// Renders one checkbox row per bucket × mode. The brain reads the same
// shape on the server every turn; toggling a bucket off here hides the
// matching tools from Sonnet's palette mid-conversation. Defaults are
// "all on" for Customers / Vehicles / Appointments (Emil's must-work-
// today trio); the server resolver fills any missing key from defaults
// so the UI always renders a complete grid.

function EricPermissionsTab({ readOnly }: { readOnly: boolean }) {
  const { t } = useTranslation('settings')
  const { t: tCommon } = useTranslation('common')
  const { getToken, isLoaded } = useAuth()
  const qc = useQueryClient()

  const [form, setForm] = useState<EricPermissions>(DEFAULT_PERMISSIONS)
  const [hydrated, setHydrated] = useState(false)
  const [saveError, setSaveError] = useState<string | null>(null)

  const permsQuery = useQuery({
    queryKey: ['settings', 'eric-permissions'],
    enabled: isLoaded,
    queryFn: async () => {
      const token = await getToken()
      if (!token) throw new Error(tCommon('error.missing_token'))
      const res = await fetch('/api/shop/eric-permissions', {
        headers: { Authorization: `Bearer ${token}` },
      })
      if (!res.ok) {
        const text = await res.text().catch(() => '')
        throw new Error(
          text || tCommon('error.request_failed', { status: res.status }),
        )
      }
      return (await res.json()) as EricPermissions
    },
  })

  // Hydrate once on first load. Subsequent refetches (after save) come
  // through the mutation's onSuccess so the form already reflects the
  // user's just-saved state; resetting from refetch would flicker.
  // In-render setState — React discards the current paint and
  // re-renders with the hydrated form.
  if (permsQuery.data && !hydrated) {
    setForm(permsQuery.data)
    setHydrated(true)
  }

  const saveMutation = useMutation({
    mutationFn: async (payload: EricPermissions) => {
      const token = await getToken()
      if (!token) throw new Error(tCommon('error.missing_token'))
      const res = await fetch('/api/shop/eric-permissions', {
        method: 'PATCH',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      })
      if (!res.ok) {
        const text = await res.text().catch(() => '')
        throw new Error(text || t('eric_permissions.error_save'))
      }
      return (await res.json()) as { ok: boolean; permissions: EricPermissions }
    },
    onSuccess: (data) => {
      qc.setQueryData(['settings', 'eric-permissions'], data.permissions)
      setForm(data.permissions)
      setSaveError(null)
    },
    onError: (err) => {
      setSaveError(err instanceof Error ? err.message : String(err))
    },
  })

  const toggle = (key: keyof EricPermissions, mode: 'read' | 'write') => {
    if (readOnly) return
    setForm((prev) => {
      const next = { ...prev }
      const current = next[key] as Record<string, boolean>
      const updated = { ...current, [mode]: !current[mode] }
      return { ...next, [key]: updated } as EricPermissions
    })
  }

  if (permsQuery.isLoading) {
    return (
      <Card className="rounded-[14px] ring-border p-5 text-sm text-muted-foreground">
        {tCommon('loading')}
      </Card>
    )
  }
  if (permsQuery.isError) {
    return (
      <Card className="rounded-[14px] ring-border p-5 text-sm text-destructive">
        {permsQuery.error instanceof Error
          ? permsQuery.error.message
          : t('eric_permissions.error')}
      </Card>
    )
  }

  return (
    <div className="space-y-4">
      <Card className="rounded-[14px] ring-border p-5">
        <div>
          <h2 className="text-base font-semibold text-foreground">
            {t('eric_permissions.header')}
          </h2>
          <p className="mt-1 text-xs text-muted-foreground">
            {t('eric_permissions.subtitle')}
          </p>
        </div>

        {readOnly ? (
          <div className="mt-4 rounded-[10px] border border-border bg-muted p-3 text-xs text-muted-foreground">
            {t('eric_permissions.read_only_hint')}
          </div>
        ) : null}

        <div className="mt-5 space-y-3">
          {BUCKET_ORDER.map((bucket) => (
            <div
              key={bucket.key}
              className="rounded-[10px] border border-border bg-background p-4"
            >
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="font-medium text-foreground">
                    {t(`eric_permissions.buckets.${bucket.key}.name`)}
                  </div>
                  <p className="mt-0.5 text-xs text-muted-foreground">
                    {t(`eric_permissions.buckets.${bucket.key}.description`)}
                  </p>
                </div>
                <div className="flex shrink-0 flex-row gap-4">
                  {bucket.modes.map((mode) => {
                    const checked = (form[bucket.key] as Record<string, boolean>)[
                      mode
                    ]
                    return (
                      <label
                        key={mode}
                        className="inline-flex cursor-pointer items-center gap-2 text-sm"
                      >
                        <input
                          type="checkbox"
                          checked={checked}
                          onChange={() => toggle(bucket.key, mode)}
                          disabled={readOnly || saveMutation.isPending}
                          className="size-4 rounded border-border accent-foreground"
                        />
                        <span className="text-foreground">
                          {t(`eric_permissions.modes.${mode}`)}
                        </span>
                      </label>
                    )
                  })}
                </div>
              </div>
            </div>
          ))}
        </div>

        {saveError ? (
          <div className="mt-4 rounded-[10px] border border-destructive/30 bg-destructive/10 p-3 text-sm text-destructive">
            {saveError}
          </div>
        ) : null}

        {saveMutation.isSuccess ? (
          <div className="mt-4 rounded-[10px] border border-border bg-muted p-3 text-sm text-foreground">
            {t('eric_permissions.saved')}
          </div>
        ) : null}

        {!readOnly ? (
          <div className="mt-5 flex items-center gap-3">
            <Button
              type="button"
              onClick={() => {
                setSaveError(null)
                saveMutation.mutate(form)
              }}
              disabled={saveMutation.isPending}
            >
              {saveMutation.isPending ? (
                <>
                  <Loader2 className="size-4 animate-spin" />
                  {t('eric_permissions.saving')}
                </>
              ) : (
                t('eric_permissions.save')
              )}
            </Button>
          </div>
        ) : null}
      </Card>
    </div>
  )
}

// ─────────────────────────────────────────────────────────────────────
// Notifications — placeholder

function deviceLabel(ua: string | null, fallback: string): string {
  if (!ua) return fallback
  const browser = /Edg\//.test(ua)
    ? 'Edge'
    : /Firefox\//.test(ua)
      ? 'Firefox'
      : /Chrome\//.test(ua)
        ? 'Chrome'
        : /Safari\//.test(ua)
          ? 'Safari'
          : fallback
  const os = /Android/.test(ua)
    ? 'Android'
    : /iPhone|iPad|iPod/.test(ua)
      ? 'iPhone'
      : /Macintosh/.test(ua)
        ? 'Mac'
        : /Windows/.test(ua)
          ? 'Windows'
          : null
  return os ? `${browser} · ${os}` : browser
}

function NotificationsTab() {
  const { t } = useTranslation('settings')
  const { t: tCommon } = useTranslation('common')
  const { getToken, isLoaded } = useAuth()
  const queryClient = useQueryClient()

  // Re-read Notification.permission after enable attempts (it flips to
  // "denied" when the user declines the prompt — the tab re-renders into
  // the denied state instead of showing a failed toggle).
  const [permission, setPermission] = useState(pushPermission())
  const [testState, setTestState] = useState<'idle' | 'sent'>('idle')

  const supported = isPushSupported()
  const iosNotInstalled = isIosSafariNotInstalled()

  const infoQuery = useQuery({
    queryKey: ['settings', 'push-subscription'],
    enabled: isLoaded && supported,
    queryFn: async () => {
      const token = await getToken()
      if (!token) throw new Error(tCommon('error.missing_token'))
      const res = await fetch('/api/push/subscription', {
        headers: { Authorization: `Bearer ${token}` },
      })
      if (!res.ok) {
        throw new Error(tCommon('error.request_failed', { status: res.status }))
      }
      return (await res.json()) as PushSubscriptionInfo
    },
  })

  const endpointQuery = useQuery({
    queryKey: ['settings', 'push-endpoint'],
    enabled: supported,
    queryFn: currentSubscriptionEndpoint,
  })

  const currentEndpoint = endpointQuery.data ?? null
  const rows = infoQuery.data?.subscriptions ?? []
  const thisDeviceRow = rows.find((r) => r.endpoint === currentEndpoint) ?? null
  const enabled = thisDeviceRow !== null

  function invalidate() {
    queryClient.invalidateQueries({ queryKey: ['settings', 'push-subscription'] })
    queryClient.invalidateQueries({ queryKey: ['settings', 'push-endpoint'] })
  }

  const enableMutation = useMutation({
    mutationFn: async () => {
      const vapidKey = infoQuery.data?.vapid_public_key
      if (!vapidKey) throw new Error('not_configured')
      // Permission prompt + subscribe ride this tap's user gesture.
      const subscription = await subscribeThisDevice(vapidKey)
      const token = await getToken()
      if (!token) throw new Error(tCommon('error.missing_token'))
      const res = await fetch('/api/push/subscriptions', {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'content-type': 'application/json',
        },
        body: JSON.stringify(subscription),
      })
      if (!res.ok) {
        throw new Error(tCommon('error.request_failed', { status: res.status }))
      }
    },
    onSettled: () => {
      setPermission(pushPermission())
      invalidate()
    },
  })

  const disableMutation = useMutation({
    mutationFn: async () => {
      const endpoint = await unsubscribeThisDevice()
      if (!endpoint) return
      const token = await getToken()
      if (!token) throw new Error(tCommon('error.missing_token'))
      const res = await fetch('/api/push/subscriptions', {
        method: 'DELETE',
        headers: {
          Authorization: `Bearer ${token}`,
          'content-type': 'application/json',
        },
        body: JSON.stringify({ endpoint }),
      })
      if (!res.ok) {
        // The browser-side unsubscribe already happened; a failed server
        // DELETE means the device row is still listed — say so instead of
        // silently showing a ghost entry until the next send prunes it.
        throw new Error(tCommon('error.request_failed', { status: res.status }))
      }
    },
    onSettled: invalidate,
  })

  const testMutation = useMutation({
    mutationFn: async () => {
      const token = await getToken()
      if (!token) throw new Error(tCommon('error.missing_token'))
      const res = await fetch('/api/push/test', {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      })
      if (!res.ok) {
        throw new Error(tCommon('error.request_failed', { status: res.status }))
      }
    },
    onSuccess: () => {
      setTestState('sent')
      window.setTimeout(() => setTestState('idle'), 4000)
    },
  })

  const busy = enableMutation.isPending || disableMutation.isPending

  return (
    <Card className="rounded-[14px] ring-border p-6">
      <div className="text-base font-semibold text-foreground">
        {t('notifications.title')}
      </div>
      <p className="mt-1 max-w-md text-sm text-muted-foreground">
        {t('notifications.body')}
      </p>

      {!supported && !iosNotInstalled ? (
        <p className="mt-5 text-sm text-muted-foreground">
          {t('notifications.unsupported')}
        </p>
      ) : iosNotInstalled ? (
        <div className="mt-5 rounded-[10px] bg-surface px-4 py-3.5">
          <div className="text-sm font-medium text-foreground">
            {t('notifications.ios_install_title')}
          </div>
          <p className="mt-1 text-sm text-muted-foreground">
            {t('notifications.ios_install_body')}
          </p>
        </div>
      ) : permission === 'denied' ? (
        <p className="mt-5 text-sm text-muted-foreground">
          {t('notifications.denied')}
        </p>
      ) : infoQuery.isPending ? (
        <div className="mt-5 flex items-center gap-2 text-sm text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin" />
          {tCommon('loading')}
        </div>
      ) : infoQuery.data && infoQuery.data.vapid_public_key === null ? (
        <p className="mt-5 text-sm text-muted-foreground">
          {t('notifications.unavailable')}
        </p>
      ) : infoQuery.data ? (
        <>
          <div className="mt-5 flex min-h-11 items-center justify-between gap-3">
            <Label
              htmlFor="push-toggle"
              className="text-sm font-medium text-foreground"
            >
              {t('notifications.toggle_label')}
            </Label>
            <div className="flex items-center gap-2.5">
              {busy ? (
                <span className="text-[13px] text-ink-3">
                  {t('notifications.working')}
                </span>
              ) : null}
              <Switch
                id="push-toggle"
                checked={enabled}
                disabled={busy}
                onCheckedChange={(next) => {
                  if (next) enableMutation.mutate()
                  else disableMutation.mutate()
                }}
              />
            </div>
          </div>
          {enableMutation.isError &&
          (enableMutation.error as Error).message === 'permission_denied' ? null : enableMutation.isError ? (
            <p className="mt-2 text-[13px] font-medium text-destructive">
              {t('notifications.error_enable')}
            </p>
          ) : null}
          {disableMutation.isError ? (
            <p className="mt-2 text-[13px] font-medium text-destructive">
              {t('notifications.error_generic')}
            </p>
          ) : null}

          {enabled ? (
            <div className="mt-3">
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="min-h-11"
                disabled={testMutation.isPending}
                onClick={() => testMutation.mutate()}
              >
                {testMutation.isPending
                  ? t('notifications.test_sending')
                  : t('notifications.test_button')}
              </Button>
              {testState === 'sent' ? (
                <span className="ml-3 text-[13px] text-muted-foreground">
                  {t('notifications.test_sent')}
                </span>
              ) : null}
              {testMutation.isError ? (
                <span className="ml-3 text-[13px] font-medium text-destructive">
                  {t('notifications.error_generic')}
                </span>
              ) : null}
            </div>
          ) : null}

          {rows.length > 0 ? (
            <div className="mt-6">
              <div className="text-[13px] font-semibold text-muted-foreground">
                {t('notifications.devices_title')}
              </div>
              <ul className="mt-2 divide-y divide-border">
                {rows.map((r) => (
                  <li key={r.id} className="flex min-h-11 items-center justify-between gap-3 py-2.5">
                    <div className="min-w-0">
                      <div className="truncate text-sm text-foreground">
                        {deviceLabel(r.user_agent, t('notifications.unknown_device'))}
                        {r.endpoint === currentEndpoint ? (
                          <span className="ml-2 rounded-full bg-surface-2 px-2 py-0.5 text-[11px] font-medium text-muted-foreground">
                            {t('notifications.this_device')}
                          </span>
                        ) : null}
                      </div>
                      <div className="truncate text-[13px] text-ink-3">
                        {t('notifications.added', {
                          when: formatRelative(r.created_at),
                        })}
                        {' · '}
                        {r.last_success_at
                          ? t('notifications.last_delivered', {
                              when: formatRelative(r.last_success_at),
                            })
                          : t('notifications.never_delivered')}
                      </div>
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          ) : null}
        </>
      ) : (
        <p className="mt-5 text-sm text-destructive">
          {t('notifications.error_load')}
        </p>
      )}
    </Card>
  )
}
